<script lang="ts" context="module">
  import { readable, writable } from "svelte/store"
  import { requestCameraPermission } from "@/libs/camera"

  /** Store with the current camera permission status */
  export const permission = writable<PermissionState>("prompt", (set) => {
    if (!("permissions" in navigator)) return

    const permission = navigator.permissions
      .query({ name: "camera" as PermissionName })
      .then((permission) => {
        permission.onchange = () => set(permission.state)

        set(permission.state)

        return permission
      })
      .catch(() => {})

    return () =>
      permission.then((permission) => {
        if (permission) permission.onchange = null
      })
  })

  // As of June 2023, the Permissions API is not supported by Firefox and Safari < 16
  const isLegacyPermissionsAPI = readable(false, (set) => {
    if (!("permissions" in navigator)) return set(true)

    navigator.permissions.query({ name: "camera" as PermissionName }).catch(() => set(true))
  })
</script>

<script lang="ts">
  import { createEventDispatcher } from "svelte"
  import { t } from "@/libs/i18n"
  import { Button, fade } from "@/libs/ui"
  import CameraAccessDeniedIcon from "./img/camera-access-denied.svg?icon"
  import CameraLgIcon from "./img/camera-lg.svg?icon"
  import CameraIcon from "./img/camera.svg?icon"
  import UploadLgIcon from "./img/upload-lg.svg?icon"
  import UploadIcon from "./img/upload.svg?icon"

  export let sources: Array<`${string}camera${string}` | "upload"> = []

  const dispatch = createEventDispatcher<{
    camera: void
    upload: File
  }>()

  let isRequestingCameraPermission = false

  const onCameraRequest = async () => {
    isRequestingCameraPermission = true
    const granted = await requestCameraPermission()
    isRequestingCameraPermission = false

    $permission = granted ? "granted" : "denied"

    if (granted) dispatch("camera")
  }

  const onFileUpload = ({ currentTarget }: { currentTarget: HTMLInputElement }) => {
    const file = currentTarget.files?.[0]

    if (file) dispatch("upload", file)

    currentTarget.value = "" // reset the input value to allow uploading the same file again, https://stackoverflow.com/a/60886920
  }

  $: hasCameraOption = sources.find((source) => source.includes("camera")) // match "camera", "front_camera", etc
  $: hasUploadOption = sources.includes("upload")

  $: isCameraPermissionDenied = $permission === "denied"
  $: isReloadRequiredForCameraPermission = $isLegacyPermissionsAPI

  $: isCameraDisabled = isRequestingCameraPermission || isCameraPermissionDenied
  $: isUploadDisabled = isRequestingCameraPermission
</script>

<section class:source-selection={/* anchor for external customization */ true}>
  {#if hasCameraOption && isCameraPermissionDenied}
    <article class="warning" in:fade={{ duration: 350 }}>
      <div class:figure={/* anchor for external customization */ true}>
        <CameraAccessDeniedIcon />
      </div>
      <h2>{$t.source_selection.camera_permissions_warning.title}</h2>
      <p>
        {isReloadRequiredForCameraPermission
          ? $t.source_selection.camera_permissions_warning.instructions_reload
          : $t.source_selection.camera_permissions_warning.instructions_reset}
      </p>
    </article>
  {:else}
    <article class="prompt" in:fade={{ duration: 350 }}>
      <div class:figure={/* anchor for external customization */ true}>
        <CameraLgIcon />
        <UploadLgIcon />
      </div>
      <h2>{$t.source_selection.title}</h2>
    </article>
  {/if}
  <form on:submit|preventDefault>
    {#if hasCameraOption}
      <Button on:click={isCameraDisabled ? null : onCameraRequest} disabled={isCameraDisabled}>
        <CameraIcon />
        <span>{$t.source_selection.camera_video}</span>
      </Button>
    {/if}
    {#if hasUploadOption}
      <Button as="label" disabled={isUploadDisabled} secondary>
        <UploadIcon />
        <span>{$t.source_selection.upload_photo}</span>
        <input type="file" accept="image/*" on:input={onFileUpload} disabled={isUploadDisabled} />
      </Button>
    {/if}
  </form>
</section>

<style lang="postcss">
  section {
    @apply flex h-full w-full flex-col items-center p-4;
  }

  :global([data-ui-scale="lg"]) section,
  :global([data-ui-scale="xl"]) section {
    @apply p-10;
  }
  :global([data-ui-scale="xl"]) section {
    @apply mx-auto max-w-[42.5rem];
  }

  article {
    @apply my-auto flex flex-col items-center justify-center gap-2;

    & > div {
      @apply mb-4 h-16 w-16 overflow-hidden rounded-[18px] p-2 text-white;
    }
  }

  :global([data-ui-scale="lg"]) article > div,
  :global([data-ui-scale="xl"]) article > div {
    @apply mb-10;
  }

  .prompt > div {
    @apply flex animate-[6.5s_linear_infinite_gradient];

    & > :global(*) {
      @apply shrink-0 basis-full;
    }

    & > :global(:first-child) {
      @apply animate-[6.5s_linear_600ms_infinite_slide-1];
    }
    & > :global(:last-child) {
      @apply animate-[6.5s_linear_600ms_infinite_slide-2] opacity-0;
    }

    /**
     * Total time - 6500ms
     *
     * Gradient
     *  - green - 3000ms
     *  - green -> semi-transparent white  - 600ms
     *    - green -> transparent white - 350ms
     *    - transparent white -> semi-transparent white - 250ms
     *  - semi-transparent white - 2650ms 
     *  - semi-transparent white -> green - 250ms
    */
    @keyframes gradient {
      0%, /* 0/6000 */
      46.15% /* 3000/6500 */ {
        background-color: #50d585;
      }
      52.53% /* 3350/6500 */ {
        background-color: rgba(var(--color) / 0.1);
      }
      55.38%, /* 3600/6500 */
      96.15% /* 6250/6500 */ {
        background-color: rgba(var(--color) / 0.2);
      }
      100% /* 6000/6000 */ {
        background-color: #50d585;
      }
    }

    /**
     * Total time - 6500ms
     *
     * Slide
     *  - delay 600ms
     *  - camera - 2400ms
     *  - camera -> gallery - 250ms
     *
     *  - gallery bounce - 600ms
     *  - gallery - 2400ms
     *  - gallery -> camera - 250ms
     *
     *  - camera bounce - 600ms
     *  - camera - 2400ms
     */
    @keyframes slide-1 {
      0%, /* 0/6000 */
      36.92%  /* 2400/6500 */ {
        transform: translateX(0);
        opacity: 1;
      }

      /* Slide forward */
      40.76% /* 2650/6500 */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /** Bounce */
      42.30% /* 2750/6500 */ {
        transform: translateX(-114%);
        opacity: 1;
      }
      42.301% /* next tick */ {
        transform: translateX(-114%);
        opacity: 0;
      }
      46.15% /* 3000/6500 */ {
        transform: translateX(-93%);
        opacity: 0;
      }
      50%, /* 3250/6500 */
      86.92% /* 5650/6500 */ {
        transform: translateX(-100%);
        opacity: 0;
      }
      86.921% /* next tick */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /* Slide backward */
      90.76% /* 5900/6500 */ {
        transform: translateX(0%);
        opacity: 1;
      }
      /** Bounce */
      92.30% /* 6000/6500 */ {
        transform: translateX(14%);
        opacity: 1;
      }
      96.15% /* 6250/6500 */ {
        transform: translateX(-7%);
        opacity: 1;
      }
      100% /* 6000/6500 */ {
        transform: translateX(0%);
        opacity: 1;
      }
    }
    /**
     * Total time - 6500ms
     *
     * Slide
     *  - delay 600ms
     *  - camera - 2400ms
     *  - camera -> gallery - 250ms
     *
     *  - gallery bounce - 600ms
     *  - gallery - 2400ms
     *  - gallery -> camera - 250ms
     *
     *  - camera bounce - 600ms
     *  - camera - 2400ms
     */
    @keyframes slide-2 {
      0%, /* 0/6000 */
      36.92%  /* 2400/6500 */ {
        transform: translateX(0);
        opacity: 0;
      }
      36.921%  /* next tick */ {
        transform: translateX(0);
        opacity: 1;
      }

      /* Slide forward */
      40.76% /* 2650/6500 */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /** Bounce */
      42.30% /* 2750/6500 */ {
        transform: translateX(-114%);
        opacity: 1;
      }
      46.15% /* 3000/6500 */ {
        transform: translateX(-93%);
        opacity: 1;
      }
      50%, /* 3250/6500 */
      86.92% /* 5650/6500 */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /* Slide backward */
      90.76% /* 5900/6500 */ {
        transform: translateX(0%);
        opacity: 1;
      }

      /** Bounce */
      92.30% /* 6000/6500 */ {
        transform: translateX(14%);
        opacity: 1;
      }
      92.301% /* next tick */ {
        transform: translateX(14%);
        opacity: 0;
      }
      96.15% /* 6250/6500 */ {
        transform: translateX(-7%);
        opacity: 0;
      }
      100% /* 6000/6500 */ {
        transform: translateX(0%);
        opacity: 0;
      }
    }
  }

  .warning > div {
    @apply bg-[#EB5545];
  }

  h2 {
    @apply text-center;
  }

  p {
    @apply max-w-[30ch] text-center opacity-60;
  }

  :global([data-ui-scale="xs"]) p,
  :global([data-ui-scale="sm"]) p {
    @apply max-w-[25ch];
  }
  :global([data-ui-scale="lg"]) p,
  :global([data-ui-scale="xl"]) p {
    @apply max-w-[35ch];
  }

  form {
    @apply flex w-full flex-col gap-2;
  }

  :global([data-ui-scale="lg"]) form,
  :global([data-ui-scale="xl"]) form {
    @apply gap-3;
  }

  input[type="file"] {
    @apply absolute hidden;
  }
</style>
